import React from 'react';
import {
  useLocation,
  useHistory,
  Switch,
  Route,
  Redirect,
} from 'react-router-dom';
import { Select } from 'components/FormFields';
import {
  PageShell,
  PageHeader,
  SegmentedFilter,
  useIsMobile,
} from 'components/v3';
import { useGetAdminTopics, useGetAdminVideos } from './gql';
import SettingsPage from './pages/SettingsPage';
import TopicsPage from './pages/TopicsPage';
import VideosPage from './pages/VideosPage';
import {
  TabsCard,
  TabContainer,
  StatsContainer,
  MiniStatCard,
  MiniStatValue,
  MiniStatLabel,
} from './StyledComponents';

const LearningHubAdmin = () => {
  const location = useLocation();
  const history = useHistory();
  const isMobile = useIsMobile();

  // Determine route prefix
  const { pathname } = location;
  const isTrackAdmin = pathname?.includes('admin-track');
  const isEmployeeAdmin = pathname?.includes('admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const basePath = `${routePrefix}/learning-hub-admin`;

  // Derive active tab from URL
  const lastSegment = pathname.split('/').pop();
  const currentTab = ['videos', 'topics', 'settings'].includes(lastSegment)
    ? lastSegment
    : 'videos';

  const navigateToTab = (tab) => {
    history.push(`${basePath}/${tab}`);
  };

  const { topics } = useGetAdminTopics();
  const { videos, count: videoCount } = useGetAdminVideos({});

  const publishedCount = videos.filter((v) => v.status === 'published').length;
  const draftCount = videos.filter((v) => v.status === 'draft').length;
  const discoverCount = videos.filter((v) => v.mode === 'discover').length;
  const howtoCount = videos.filter((v) => v.mode === 'howto').length;

  return (
    <PageShell>
      <PageHeader icon="settings-2" title="Learning Hub Manager" />

      <TabsCard>
        <TabContainer>
          {isMobile ? (
            <Select
              value={currentTab}
              onChange={(e) => navigateToTab(e.target.value || 'videos')}
              options={[
                { label: `Videos (${videoCount})`, value: 'videos' },
                { label: `Topics (${topics.length})`, value: 'topics' },
                { label: 'Settings', value: 'settings' },
              ]}
            />
          ) : (
            <SegmentedFilter
              value={currentTab}
              onChange={navigateToTab}
              options={[
                { key: 'videos', label: 'Videos', count: videoCount },
                { key: 'topics', label: 'Topics', count: topics.length },
                { key: 'settings', label: 'Settings' },
              ]}
            />
          )}
          <StatsContainer>
            <MiniStatCard>
              <MiniStatValue color="#34D399">{publishedCount}</MiniStatValue>
              <MiniStatLabel>Published</MiniStatLabel>
            </MiniStatCard>
            <MiniStatCard>
              <MiniStatValue color="#94A3B8">{draftCount}</MiniStatValue>
              <MiniStatLabel>Drafts</MiniStatLabel>
            </MiniStatCard>
            <MiniStatCard>
              <MiniStatValue color="#A78BFA">{discoverCount}</MiniStatValue>
              <MiniStatLabel>Ideas & Tips</MiniStatLabel>
            </MiniStatCard>
            <MiniStatCard>
              <MiniStatValue color="#60A5FA">{howtoCount}</MiniStatValue>
              <MiniStatLabel>How To Do It</MiniStatLabel>
            </MiniStatCard>
          </StatsContainer>
        </TabContainer>
      </TabsCard>

      <Switch>
        <Route path={`${basePath}/videos`}>
          <VideosPage />
        </Route>
        <Route path={`${basePath}/topics`}>
          <TopicsPage />
        </Route>
        <Route path={`${basePath}/settings`}>
          <SettingsPage />
        </Route>
        <Redirect from={basePath} to={`${basePath}/videos`} />
      </Switch>
    </PageShell>
  );
};

export default LearningHubAdmin;
